<template>
  <div class="strategy-page boss-page">
    <van-nav-bar class="strategy-nav" title="战略落地" left-arrow @click-left="$router.back()"/>

    <section class="hero">
      <div class="hero-kicker">Boss 决策台</div>
      <h1>把方向落到每天的行动</h1>
      <p>老板只需要确认方向、创建项目、查看执行结果。AI 负责拆解，主管负责分配，员工负责执行和回传成果。</p>
    </section>

    <main>
      <section class="step-card source-card">
        <div class="step-head"><i>1</i>
          <div><b>提供战略参考</b><small>仅作为 AI 分析和起草的参考，不会直接发布为公司战略</small></div>
          <span class="state">可选</span></div>
        <div class="source-tabs">
          <button :class="{active: sourceMode === 'knowledge'}" @click="sourceMode = 'knowledge'">知识库资料</button>
          <button :class="{active: sourceMode === 'voice'}" @click="sourceMode = 'voice'">语音 / 文字</button>
        </div>
        <template v-if="sourceMode === 'knowledge'">
          <div v-if="knowledgeLoading" class="loading">
            <van-loading size="16px"/>
            正在读取战略资料
          </div>
          <div v-else-if="knowledgeDocs.length" class="doc-list">
            <button v-for="(doc,index) in knowledgeDocs" :key="doc.id || doc.documentId || index" class="doc-item"
                    :class="{selected: selectedDocKey === (doc.id || doc.documentId || index)}" @click="selectDoc(doc,index)">
              <van-icon name="description"/>
              <span><b>{{ doc.title }}</b><small>{{ doc.category || '知识资料' }} · {{ doc.summary || '已进入知识库，可作为战略参考' }}</small></span>
              <van-icon :name="selectedDocKey === (doc.id || doc.documentId || index) ? 'passed' : 'arrow'"/>
            </button>
          </div>
          <div v-else class="empty">还没有匹配的战略资料，先上传一份公司或事业部战略材料。</div>
          <van-button class="full-action" block plain type="danger" icon="upgrade" @click="$router.push({name:'BossKnowledgeUpload',query:{category:'战略资料'}})">
            上传战略资料
          </van-button>
        </template>
        <boss-voice-input v-else v-model="sourceText" @confirmed="sourceConfirmed"/>
        <textarea v-if="sourceMode === 'knowledge'" v-model.trim="sourceText" class="source-text" maxlength="3000"
                  placeholder="选中资料后可补充本阶段重点，也可以直接粘贴文字"/>
        <div v-if="sourceText" class="selected-note">
          <van-icon name="passed"/>
          已形成战略依据，可继续确认战略层级
        </div>
        <van-button v-if="sourceText" block type="danger" plain icon="edit" :loading="draftLoading" @click="generateDraft">根据素材生成战略草稿
        </van-button>
        <div v-if="draftLoading" class="draft-progress">
          <div v-for="(stage, i) in draftStages" :key="stage" class="draft-stage">
            <span :class="{done: i < draftStage, active: i === draftStage}">{{ stage }}</span>
            <em v-if="i < draftStages.length - 1">→</em>
          </div>
          <button class="draft-cancel" @click="cancelDraft">取消</button>
        </div>
        <div v-if="draftEvidence.length && !draftLoading" class="evidence-board">
          <div class="evidence-head"><van-icon name="notes-o"/>依据来源（{{ draftEvidence.length }}）</div>
          <ul class="evidence-list">
            <li v-for="ev in draftEvidence" :key="ev.index" class="evidence-item" :class="{kb: ev.source==='knowledge-base'}">
              <span class="ev-index">{{ ev.source==='knowledge-base' ? '库' : ev.index }}</span>
              <div class="ev-body">
                <div class="ev-title">{{ ev.title }}<span class="ev-tag">{{ ev.source==='knowledge-base' ? '知识库补充' : '战略参考素材' }}</span></div>
                <p class="ev-snippet">{{ ev.snippet }}</p>
                <div v-if="ev.referencedBy.length" class="ev-ref">被关键结果 #{{ ev.referencedBy.map(i=>i+1).join('、#') }} 引用</div>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section class="step-card strategy-card">
        <div class="step-head"><i>2</i>
          <div><b>维护并发布战略版本</b><small>老板在这里正式创建、关联、编辑和发布公司战略、事业部战略、事业部 OKR</small></div>
          <span class="state ready">核心</span></div>
        <div v-if="mapLoading" class="loading">
          <van-loading size="16px"/>
          正在读取当前战略版本
        </div>
        <template v-else>
          <div class="strategy-summary"><span><b>{{ companyStrategies.length }}</b> 个公司战略</span><span><b>{{ buStrategies.length }}</b> 个事业部战略</span><span><b>{{
              buOkrs.length
            }}</b> 个事业部 OKR</span></div>
          <div v-if="strategyGaps.length" class="warning">
            <van-icon name="warning-o"/>
            {{ strategyGaps.length }} 个战略节点还没有下游承接，建议先补齐方向。
          </div>
          <div class="maintenance-head">
            <div><b>战略版本</b><small>草稿不会影响项目创建，发布后才可被项目承接</small></div>
          </div>
          <van-button class="full-action" block type="danger" icon="plus" @click="createGovernanceNode">新增版本</van-button>
          <div v-if="managedNodes.length" class="managed-list">
            <div v-for="item in managedNodes" :key="item.id" class="managed-item">
              <div class="managed-main"><b>{{ item.name }}</b><small>{{ typeLabel(item.node_type || item.nodeType) }} ·
                {{ item.status === 'ACTIVE' ? '已发布' : item.status === 'INACTIVE' ? '已停用' : '草稿' }}</small></div>
              <div class="managed-actions">
                <button @click="editGovernanceNode(item)">编辑</button>
                <button v-if="item.status !== 'ACTIVE'" @click="publishGovernanceNode(item)">发布</button>
                <button v-else @click="deactivateGovernanceNode(item)">停用</button>
              </div>
            </div>
          </div>
          <div v-else class="empty">还没有战略版本，点击“新增”创建公司战略、事业部战略或 OKR。</div>
          <button class="link-row" @click="$router.push({name:'BossGovernanceCenter'})">
            <van-icon name="shield-o"/>
            <span><b>维护公司战略与安全阀</b><small>老板的战略、政策和规则仍以治理中心为准</small></span>
            <van-icon name="arrow"/>
          </button>
          <button class="link-row" @click="$router.push({name:'StrategyActionMap'})">
            <van-icon name="cluster-o"/>
            <span><b>查看战略行动地图</b><small>检查战略是否已经承接到 OKR、项目和任务</small></span>
            <van-icon name="arrow"/>
          </button>
        </template>
      </section>

      <section class="step-card action-card">
        <div class="step-head"><i>3</i>
          <div><b>把战略变成项目</b><small>项目由 Boss 创建，并选择承接的战略或事业部 OKR</small></div>
          <span class="state ready">可操作</span></div>
        <van-button block type="danger" icon="plus" @click="$router.push({name:'BossProjectPortfolio',query:{create:'1'}})">根据战略创建项目
        </van-button>
        <button class="link-row" @click="$router.push({name:'BossProjectPortfolio'})">
          <van-icon name="chart-trending-o"/>
          <span><b>查看项目执行进度</b><small>进入项目后，AI 拆分任务，人工复审并交给主管分配</small></span>
          <van-icon name="arrow"/>
        </button>
      </section>

      <section class="step-card execution-card">
        <div class="step-head"><i>4</i>
          <div><b>看执行与结果</b><small>后续环节由 AI、主管和员工协同完成，Boss 只处理需要决策的事项</small></div>
        </div>
        <div class="execution-flow">
          <span><b>AI</b><small>拆任务</small></span><em>→</em><span><b>主管</b><small>复审分配</small></span><em>→</em><span><b>员工</b><small>工作要点</small></span><em>→</em><span><b>结果</b><small>复盘回流</small></span>
        </div>
        <button class="link-row" @click="$router.push({name:'BossProjectPortfolio'})">
          <van-icon name="bar-chart-o"/>
          <span><b>进入项目投效与结果复盘</b><small>查看任务、成果、风险和资源冲突</small></span>
          <van-icon name="arrow"/>
        </button>
      </section>
      <p class="note">这里管理的是“每天做事的方向是否正确”。月度绩效考核仍按原有独立标准计算，两条业务线互相提供证据但不混为一个分数。</p>
    </main>
    <van-popup v-model="editorVisible" position="center" round get-container="body" class="strategy-editor">
      <div class="editor-title"><span>{{ editingNode ? '编辑战略版本' : '新增战略版本' }}</span><small>填写后先保存为草稿，确认无误再发布</small></div>
      <van-field v-model="editor.nodeTypeLabel" label="类型" readonly clickable right-icon="arrow" @click="typePickerVisible=true"/>
      <van-field v-model="editor.name" label="名称" required placeholder="请输入战略或 OKR 名称"/>
      <van-field v-if="editor.nodeType !== 'COMPANY_STRATEGY'" v-model="editor.organizationName" label="事业部" readonly clickable required
                 right-icon="arrow" @click="openUnitPicker"/>
      <van-field v-model="editor.parentName" label="上级承接" readonly clickable right-icon="arrow"
                 placeholder="可选，建立战略层级关系" @click="parentPickerVisible=true"/>
      <van-field v-if="editor.nodeType === 'BUSINESS_UNIT_OKR'" v-model="editor.objective" label="OKR目标" type="textarea" autosize required/>
      <van-field v-if="editor.nodeType === 'BUSINESS_UNIT_OKR'" v-model="editor.keyResultsText" label="关键结果" type="textarea" autosize
                 placeholder="每行一条关键结果"/>
      <van-field v-model="editor.description" label="说明" type="textarea" autosize placeholder="补充背景、边界和判断口径"/>
      <div class="editor-actions">
        <van-button block type="danger" :loading="editorSaving" @click="saveGovernanceNode">保存草稿</van-button>
      </div>
    </van-popup>
    <van-popup v-model="typePickerVisible" position="bottom">
      <van-picker show-toolbar title="选择类型" :columns="typeOptions" @cancel="typePickerVisible=false" @confirm="chooseType"/>
    </van-popup>
    <van-popup v-model="unitPickerVisible" position="bottom">
      <van-picker show-toolbar title="选择事业部" :columns="unitOptions.map(x=>x.name)" @cancel="unitPickerVisible=false" @confirm="chooseUnit"/>
    </van-popup>
    <van-popup v-model="parentPickerVisible" position="bottom">
      <van-picker show-toolbar title="选择上级承接" :columns="parentOptions.map(x=>x.name)" @cancel="parentPickerVisible=false"
                  @confirm="chooseParent"/>
    </van-popup>
  </div>
</template>

<script>
import coreapi from '@/utils/coreapi';
import BossVoiceInput from '@/components/boss/BossVoiceInput.vue';
import {buildStrategyUnitOptions, collectBusinessUnitRows, normalizeOrganizationId, businessUnitSysno} from '@/utils/businessUnits';

export default {
  name: 'BossStrategyExecution',
  components: {BossVoiceInput},
  data() {
    return {
      sourceMode: 'knowledge',
      sourceText: '',
      knowledgeDocs: [],
      selectedDoc: null,
      selectedDocKey: null,
      knowledgeLoading: false,
      mapLoading: false,
      draftLoading: false,
      draftStage: 0,
      draftStageTimer: null,
      draftStages: ['资料解析中', '内容归纳中', '草稿生成中'],
      draftTaskId: null,
      draftEvidence: [],
      draftResult: null,
      map: {},
      management: {},
      editorVisible: false,
      editorSaving: false,
      editingNode: null,
      typePickerVisible: false,
      unitPickerVisible: false,
      parentPickerVisible: false,
      unitOptions: [],
      editor: {
        nodeType: 'COMPANY_STRATEGY',
        nodeTypeLabel: '公司战略',
        name: '',
        description: '',
        organizationId: null,
        organizationName: '',
        businessUnitSysno: null,
        parentId: null,
        parentName: '',
        objective: '',
        keyResultsText: ''
      }
    };
  },
  computed: {
    nodes() {
      return this.map.nodes || [];
    },
    nodeType() {
      return node => String(node.nodeType || node.node_type || '').toUpperCase();
    },
    companyStrategies() {
      return this.nodes.filter(x => ['COMPANY_STRATEGY', 'STRATEGY'].includes(this.nodeType(x)));
    },
    buStrategies() {
      return this.nodes.filter(x => this.nodeType(x) === 'BUSINESS_UNIT_STRATEGY');
    },
    buOkrs() {
      return this.nodes.filter(x => ['BUSINESS_UNIT_OKR', 'DEPARTMENT_GOAL'].includes(this.nodeType(x)));
    },
    strategyGaps() {
      return this.map.gaps || [];
    }
    , managedNodes() {
      return this.management.nodes || [];
    },
    typeOptions() {
      return ['公司战略', '事业部战略', '事业部 OKR'];
    },
    parentOptions() {
      const currentId = this.nodeId(this.editingNode);
      const allowedParents = {
        BUSINESS_UNIT_STRATEGY: ['COMPANY_STRATEGY', 'STRATEGY'],
        BUSINESS_UNIT_OKR: ['BUSINESS_UNIT_STRATEGY', 'COMPANY_STRATEGY', 'STRATEGY']
      };
      const allowed = allowedParents[this.editor.nodeType];
      return this.managedNodes.filter(x => {
        if (this.nodeId(x) === currentId) return false;
        const status = String(x.status || 'DRAFT').toUpperCase();
        if (status === 'INACTIVE') return false;
        if (!allowed) return true;
        const type = String(x.node_type || x.nodeType || '').toUpperCase();
        return allowed.includes(type);
      });
    }
  },
  mounted() {
    this.loadKnowledge();
    this.loadMap();
    this.loadManagement();
  },
  methods: {
    async loadKnowledge() {
      this.knowledgeLoading = true;
      try {
        const docs = await coreapi.knowledge('战略 OR 经营 OR 事业部 OR OKR', { limit: 100 });
        this.knowledgeDocs = (docs || []).slice(0, 6);
      } catch (e) {
        this.knowledgeDocs = [];
      } finally {
        this.knowledgeLoading = false;
      }
    },
    async loadMap() {
      this.mapLoading = true;
      try {
        this.map = await coreapi.strategyActionMap() || {};
      } catch (e) {
        this.$toast.fail(e.message || '战略版本读取失败');
      } finally {
        this.mapLoading = false;
      }
    },
    async loadManagement() {
      try {
        this.management = await coreapi.strategyManagement() || {};
      } catch (e) {
        this.management = {};
      }
    },
    typeLabel(type) {
      return ({
        COMPANY_STRATEGY: '公司战略',
        BUSINESS_UNIT_STRATEGY: '事业部战略',
        BUSINESS_UNIT_OKR: '事业部OKR',
        STRATEGY: '战略',
        DEPARTMENT_GOAL: '部门目标'
      })[String(type).toUpperCase()] || type;
    },
    async createGovernanceNode() {
      this.editingNode = null;
      this.editor = {
        nodeType: 'COMPANY_STRATEGY',
        nodeTypeLabel: '公司战略',
        name: '',
        description: '',
        organizationId: null,
        organizationName: '',
        businessUnitSysno: null,
        parentId: null,
        parentName: '',
        objective: '',
        keyResultsText: ''
      };
      await this.loadUnits();
      this.editorVisible = true;
    },
    async editGovernanceNode(item) {
      this.editingNode = item;
      const type = this.nodeType(item);
      this.editor = {
        nodeType: type,
        nodeTypeLabel: this.typeLabel(type),
        name: item.name || '',
        description: item.description || '',
        organizationId: this.normalizeOrganizationId(item.organization_id || item.organizationId),
        organizationName: item.organization_name || '',
        businessUnitSysno: businessUnitSysno(item.business_unit_sysno || item.businessUnitSysno),
        parentId: null,
        parentName: '',
        objective: item.objective || '',
        keyResultsText: this.keyResultsText(item.key_results_json)
      };
      await this.loadUnits();
      this.editorVisible = true;
    },
    keyResultsText(value) {
      if (!value) return '';
      if (Array.isArray(value)) return value.join('\n');
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.join('\n') : String(value);
      } catch (e) {
        return String(value);
      }
    },
    async loadUnits() {
      try {
        let rawRows = [];
        try {
          const master = await coreapi.organizationBusinessUnits();
          rawRows = collectBusinessUnitRows(master);
        } catch (e) { /* 回退组织树 */ }
        if (!rawRows.length) {
          const tree = await coreapi.organizationTree();
          rawRows = collectBusinessUnitRows(tree);
        }
        this.unitOptions = buildStrategyUnitOptions(rawRows);
      } catch (e) {
        this.unitOptions = buildStrategyUnitOptions([]);
      }
    },
    chooseType(value, index) {
      const types = ['COMPANY_STRATEGY', 'BUSINESS_UNIT_STRATEGY', 'BUSINESS_UNIT_OKR'];
      this.editor.nodeType = types[index];
      this.editor.nodeTypeLabel = value;
      this.typePickerVisible = false;
    },
    chooseUnit(value, index) {
      const x = this.unitOptions[index];
      if (x) {
        this.editor.organizationId = x.id;
        this.editor.organizationName = x.name;
        this.editor.businessUnitSysno = x.businessUnitSysno || null;
      }
      this.unitPickerVisible = false;
    },
    openUnitPicker() {
      if (!this.unitOptions.length) {
        this.$toast('事业部列表加载中，请稍后再试');
        this.loadUnits();
        return;
      }
      this.unitPickerVisible = true;
    },
    normalizeOrganizationId(value) {
      return normalizeOrganizationId(value);
    },
    chooseParent(value, index) {
      const x = this.parentOptions[index];
      if (x) {
        this.editor.parentId = this.nodeId(x);
        this.editor.parentName = x.name;
      }
      this.parentPickerVisible = false;
    },
    nodeId(item) {
      if (!item) return null;
      const raw = item.id != null ? item.id : item.ID;
      if (raw == null || raw === '') return null;
      const id = Number(raw);
      return Number.isFinite(id) ? id : null;
    },
    async saveGovernanceNode() {
      if (!this.editor.name.trim()) return this.$toast('请填写名称');
      const organizationId = this.normalizeOrganizationId(this.editor.organizationId);
      const unitSysno = this.editor.businessUnitSysno || null;
      if (this.editor.nodeType !== 'COMPANY_STRATEGY' && organizationId == null && !unitSysno && !this.editor.organizationName) {
        return this.$toast('请选择事业部');
      }
      if (this.editor.nodeType === 'BUSINESS_UNIT_OKR' && !this.editor.objective.trim()) return this.$toast('请填写 OKR 目标');
      this.editorSaving = true;
      try {
        const body = {
          nodeType: this.editor.nodeType,
          name: this.editor.name,
          description: this.editor.description,
          organizationId: this.editor.nodeType === 'COMPANY_STRATEGY' ? null : organizationId,
          organizationName: this.editor.nodeType === 'COMPANY_STRATEGY' ? null : this.editor.organizationName,
          businessUnitSysno: this.editor.nodeType === 'COMPANY_STRATEGY' ? null : unitSysno,
          status: 'DRAFT',
          objective: this.editor.objective,
          keyResultsJson: JSON.stringify(this.editor.keyResultsText.split(/\n+/).map(x => x.trim()).filter(Boolean))
        };
        if (!this.editingNode && this.editor.parentId) body.parentNodeId = this.editor.parentId;
        const result = this.editingNode ? await coreapi.updateStrategyNode(this.editingNode.id, body) : await coreapi.createStrategyNode(body);
        this.editorVisible = false;
        this.$toast.success(this.editingNode ? '已保存' : '已保存为草稿');
        await this.loadManagement();
        await this.loadMap();
      } catch (e) {
        this.$toast.fail(e.message || '保存失败');
      } finally {
        this.editorSaving = false;
      }
    },
    async publishGovernanceNode(item) {
      try {
        await coreapi.publishStrategyNode(item.id);
        this.$toast.success('已发布，项目创建页将可选择');
        await this.loadManagement();
        await this.loadMap();
      } catch (e) {
        this.$toast.fail(e.message || '发布失败');
      }
    },
    async deactivateGovernanceNode(item) {
      if (!window.confirm('停用后将不再出现在项目创建页，确认停用？')) return;
      try {
        const result = await coreapi.deactivateStrategyNode(item.id);
        const linked = Number(result && result.linkedProjects || 0);
        this.$toast.success(linked ? `已停用，已有 ${linked} 个项目保留原关联` : '已停用');
        await this.loadManagement();
        await this.loadMap();
      } catch (e) {
        this.$toast.fail(e.message || '停用失败');
      }
    },
    async selectDoc(doc, index) {
      this.selectedDoc = doc;
      this.selectedDocKey = doc.id || doc.documentId || index;
      this.sourceText = doc.summary || doc.title || '';
      try {
        const detail = await coreapi.knowledgeDetail(doc.id || doc.documentId);
        this.sourceText = detail.content || detail.summary || this.sourceText;
      } catch (e) { /* 摘要足够时允许继续 */
      }
    },
    sourceConfirmed(text) {
      this.sourceText = text;
      this.$toast.success('战略依据已形成');
    },
    async openDraftEditor(draft) {
      const nodeType = draft.nodeType || 'COMPANY_STRATEGY';
      this.editor = {
        nodeType,
        nodeTypeLabel: this.typeLabel(nodeType),
        name: draft.name || '',
        description: draft.description || '',
        organizationId: this.normalizeOrganizationId(draft.organizationId),
        organizationName: draft.organizationName || '',
        businessUnitSysno: businessUnitSysno(draft.businessUnitSysno),
        parentId: null,
        parentName: '',
        objective: draft.objective || '',
        keyResultsText: Array.isArray(draft.keyResults) ? draft.keyResults.join('\n') : ''
      };
      await this.loadUnits();
      this.editorVisible = true;
    },
    async generateDraft() {
      if (!this.sourceText || !this.sourceText.trim()) return this.$toast('请先提供战略依据');
      this.draftLoading = true;
      this.draftStage = 0;
      this.draftEvidence = [];
      this.draftResult = null;
      try {
        const created = await coreapi.createGovernanceDraftTask({
          source: this.sourceText,
          existingNodes: this.managedNodes,
          sourceTitle: this.selectedDoc && (this.selectedDoc.title || this.selectedDoc.name) || undefined
        });
        const taskId = created.taskId || (created.data && created.data.taskId);
        // 兼容当前 core-app：本地已提供同步 /strategy/draft，
        // 但异步 /strategy/draft/async 尚未注册时直接返回草稿结果。
        if (!taskId && (created.nodeType || (created.data && created.data.nodeType))) {
          const draft = created.data && created.data.nodeType ? created.data : created;
          this.draftResult = draft;
          this.draftEvidence = Array.isArray(draft.evidence) ? draft.evidence : [];
          await this.openDraftEditor(draft);
          this.draftLoading = false;
          this.$toast.success('已生成战略草稿，请审核后保存');
          return;
        }
        if (!taskId) throw new Error('未获取到任务标识');
        this.draftTaskId = taskId;
        await this.pollDraft(taskId);
      } catch (e) {
        this.$toast.fail(e.message || '草稿生成失败');
        this.draftLoading = false;
      }
    },
    pollDraft(taskId) {
      return new Promise((resolve) => {
        const timer = setInterval(async () => {
          try {
            const task = await coreapi.getGovernanceDraftTask(taskId);
            if (task.stage) {
              const idx = this.draftStages.indexOf(task.stage);
              this.draftStage = idx >= 0 ? idx : Math.min(Math.floor((task.progress || 0) * 3), 2);
            }
            if (task.status === 'SUCCESS') {
              clearInterval(timer);
              // 网关解包外层信封：task.result 为 _build 返回的 {data:草稿,...}
              const draft = (task.result && task.result.data) ? task.result.data : (task.result || {});
              this.draftResult = draft;
              this.draftEvidence = Array.isArray(draft.evidence) ? draft.evidence : [];
              await this.openDraftEditor(draft);
              const segTip = draft.sourceSegments ? `已归纳 ${draft.sourceSegments} 段资料并生成草稿` : '已生成草稿';
              const evTip = this.draftEvidence.length ? `，附 ${this.draftEvidence.length} 条依据` : '';
              this.$toast.success(`${segTip}${evTip}，请审核后保存`);
              this.draftLoading = false;
              resolve();
            } else if (task.status === 'FAILED') {
              clearInterval(timer);
              this.$toast.fail('草稿生成失败：' + (task.error || '模型返回异常'));
              this.draftLoading = false;
              resolve();
            } else if (task.status === 'CANCELLED') {
              clearInterval(timer);
              this.$toast('已取消生成');
              this.draftLoading = false;
              resolve();
            }
          } catch (e) {
            clearInterval(timer);
            this.$toast.fail(e.message || '任务查询失败');
            this.draftLoading = false;
            resolve();
          }
        }, 1500);
      });
    },
    async cancelDraft() {
      if (!this.draftTaskId) return;
      try {
        await coreapi.cancelGovernanceDraftTask(this.draftTaskId);
        this.$toast('正在取消…');
      } catch (e) {
        this.$toast.fail(e.message || '取消失败');
      }
    }
  }
};
</script>

<style lang="less" scoped>
@import './boss.less';

.strategy-page {
  min-height: 100vh;
  padding-bottom: 1.7rem;
  background: #f6f7fb;
  color: #30313a
}

.strategy-page /deep/ .van-nav-bar {
  margin: 0;
  width: 100%;
  border-radius: 0
}

.hero {
  padding: .42rem .32rem .46rem;
  color: #fff;
  background: linear-gradient(135deg, #351b1d, #c6282d)
}

.hero-kicker {
  font-size: .23rem;
  opacity: .8
}

.hero h1 {
  margin: .1rem 0;
  font-size: .42rem;
  line-height: 1.3
}

.hero p {
  margin: 0;
  font-size: .25rem;
  line-height: 1.6;
  opacity: .92
}

main {
  padding: .24rem .28rem
}

.step-card {
  margin-bottom: .2rem;
  padding: .26rem;
  background: #fff;
  border-radius: .2rem;
  box-shadow: 0 .05rem .22rem rgba(44, 31, 36, .07)
}

.source-card, .strategy-card, .action-card {
  border: 1px solid #f0e4e5;
}

.step-card > .step-head b {
  font-size: .3rem;
  color: #293044;
}

.step-card > .step-head small {
  display: block;
  margin-top: .06rem;
  color: #8b91a5;
  font-size: .2rem;
  line-height: 1.45;
}

.step-head {
  display: flex;
  align-items: flex-start;
  gap: .14rem
}

.step-head > i {
  width: .48rem;
  height: .48rem;
  flex: 0 0 .48rem;
  line-height: .48rem;
  text-align: center;
  color: #fff;
  font-size: .25rem;
  font-style: normal;
  border-radius: 50%;
  background: #d32f2f
}

.step-head > div {
  min-width: 0;
  flex: 1
}

.draft-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .14rem;
  margin-top: .18rem;
  font-size: .24rem;
  color: #999
}

.draft-stage {
  display: inline-flex;
  align-items: center;
  gap: .1rem
}

.draft-progress span.done {
  color: #2e7d32
}

.draft-progress span.active {
  color: #c6282d;
  font-weight: 600
}

.draft-progress em {
  color: #ccc;
  font-style: normal
}

.draft-cancel {
  margin-left: .14rem;
  padding: .04rem .18rem;
  border: 1PX solid #e2c7c8;
  border-radius: .2rem;
  color: #a52a2f;
  font-size: .21rem;
  background: #fff
}

.evidence-board {
  margin-top: .18rem;
  padding: .18rem;
  background: #fbfbfe;
  border: 1PX solid #eceef5;
  border-radius: .14rem
}

.evidence-head {
  display: flex;
  align-items: center;
  gap: .08rem;
  margin-bottom: .12rem;
  color: #34364a;
  font-size: .24rem;
  font-weight: 600
}

.evidence-head .van-icon {
  color: #d32f2f
}

.evidence-list {
  margin: 0;
  padding: 0;
  list-style: none
}

.evidence-item {
  display: flex;
  gap: .12rem;
  padding: .12rem 0;
  border-top: 1PX solid #f0f1f6
}

.evidence-item.kb {
  background: #f3f8ff;
  border-radius: .1rem;
  padding: .12rem .14rem;
  border-top: 0;
  margin-bottom: .1rem
}

.ev-index {
  flex: 0 0 .4rem;
  height: .4rem;
  line-height: .4rem;
  text-align: center;
  color: #fff;
  font-size: .2rem;
  border-radius: 50%;
  background: #d32f2f
}

.evidence-item.kb .ev-index {
  background: #2f6fd3
}

.ev-body {
  min-width: 0;
  flex: 1
}

.ev-title {
  display: flex;
  align-items: center;
  gap: .1rem;
  color: #34364a;
  font-size: .23rem;
  font-weight: 600
}

.ev-tag {
  padding: .02rem .1rem;
  color: #8b8ea0;
  font-size: .18rem;
  font-weight: 400;
  border: 1PX solid #e6e7ee;
  border-radius: .2rem
}

.evidence-item.kb .ev-tag {
  color: #2f6fd3;
  border-color: #cfe0fb
}

.ev-snippet {
  margin: .06rem 0 0;
  color: #6b6f82;
  font-size: .21rem;
  line-height: 1.5
}

.ev-ref {
  margin-top: .06rem;
  color: #278653;
  font-size: .2rem
}

.step-head b, .step-head small {
  display: block
}

.step-head b {
  color: #303244;
  font-size: .31rem;
  line-height: 1.35
}

.step-head small {
  margin-top: .05rem;
  color: #85899c;
  font-size: .23rem;
  line-height: 1.45
}

.state {
  padding: .06rem .12rem;
  color: #a52a2f;
  font-size: .2rem;
  white-space: nowrap;
  border-radius: .2rem;
  background: #fff1f2
}

.state.ready {
  color: #278653;
  background: #e8f7ef
}

.source-tabs {
  display: flex;
  margin: .22rem 0 .16rem;
  padding: .06rem;
  background: #f5f6fb;
  border-radius: .12rem
}

.source-tabs button {
  height: .62rem;
  flex: 1;
  border: 0;
  border-radius: .1rem;
  color: #777b90;
  font-size: .25rem;
  background: transparent
}

.source-tabs button.active {
  color: #fff;
  background: #d32f2f
}

.doc-list {
  max-height: 3.3rem;
  overflow: auto
}

.doc-item {
  width: 100%;
  padding: .16rem 0;
  display: flex;
  align-items: center;
  gap: .12rem;
  text-align: left;
  border: 0;
  border-bottom: 1PX solid #eee;
  background: #fff
}

.doc-item > .van-icon:first-child {
  color: #d32f2f;
  font-size: .32rem
}

.doc-item span {
  min-width: 0;
  flex: 1
}

.doc-item b, .doc-item small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap
}

.doc-item b {
  color: #34364a;
  font-size: .25rem
}

.doc-item small {
  margin-top: .04rem;
  color: #8b8ea0;
  font-size: .21rem
}

.doc-item > .van-icon:last-child {
  color: #aaa;
  font-size: .25rem
}

.doc-item.selected {
  background: #fff8f8
}

.doc-item.selected > .van-icon:last-child {
  color: #278653
}

.source-card > .van-button {
  margin-top: .14rem
}

.source-text {
  width: 100%;
  height: 1.1rem;
  margin-top: .14rem;
  padding: .16rem;
  box-sizing: border-box;
  border: 1PX solid #e2e3ec;
  border-radius: .1rem;
  background: #fafaff;
  font-size: .25rem;
  line-height: 1.5;
  resize: none
}

.selected-note {
  margin-top: .12rem;
  color: #278653;
  font-size: .22rem
}

.selected-note .van-icon {
  margin-right: .05rem
}

.loading {
  padding: .26rem 0;
  color: #85899c;
  font-size: .23rem;
  display: flex;
  align-items: center;
  gap: .1rem
}

.empty, .warning {
  margin-top: .16rem;
  padding: .16rem;
  color: #85899c;
  font-size: .22rem;
  line-height: 1.5;
  border-radius: .1rem;
  background: #f7f7fb
}

.warning {
  color: #a36b17;
  background: #fff8e8
}

.strategy-summary {
  display: grid;
  grid-template-columns:repeat(3, 1fr);
  gap: .08rem;
  margin: .2rem 0
}

.strategy-summary span {
  padding: .13rem .06rem;
  text-align: center;
  color: #85899c;
  font-size: .2rem;
  background: #f7f7fb;
  border-radius: .1rem
}

.strategy-summary b {
  display: block;
  color: #d32f2f;
  font-size: .34rem
}

.maintenance-head {
  display: flex;
  align-items: center;
  padding: .16rem .02rem .08rem;
}

.maintenance-head > div {
  min-width: 0;
  flex: 1;
}

.maintenance-head b,
.maintenance-head small {
  display: block;
}

.maintenance-head b {
  color: #34364a;
  font-size: .26rem;
  line-height: 1.25;
}

.maintenance-head small {
  margin-top: .06rem;
  color: #85899c;
  font-size: .18rem;
  line-height: 1.45;
}

.full-action {
  width: 100%;
  height: .62rem;
  margin: .08rem 0 .16rem;
  border-radius: .1rem;
  font-size: .25rem;
  line-height: .62rem;
}

.managed-list {
  margin-top: .08rem;
}

.managed-item {
  display: flex;
  align-items: center;
  gap: .12rem;
  margin: .1rem 0;
  padding: .16rem .14rem;
  border: 1px solid #f0edf2;
  border-radius: .12rem;
  background: #fbfbfd;
}

.managed-main {
  min-width: 0;
  flex: 1;
}

.managed-main b {
  display: block;
  overflow: hidden;
  color: #303446;
  font-size: .25rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.managed-main small {
  display: block;
  margin-top: .04rem;
  color: #9399aa;
  font-size: .19rem;
}

.managed-actions {
  display: flex;
  flex: 0 0 auto;
  gap: .08rem;
}

.managed-actions button {
  padding: .08rem .14rem;
  color: #c6282d;
  border: 1px solid #e9bfc1;
  border-radius: .18rem;
  background: #fff;
  font-size: .2rem;
}

.link-row {
  width: 100%;
  min-height: .76rem;
  padding: .14rem 0;
  display: flex;
  align-items: center;
  gap: .12rem;
  text-align: left;
  border: 0;
  border-top: 1PX solid #eee;
  background: #fff
}

.link-row > .van-icon:first-child {
  color: #d32f2f;
  font-size: .3rem
}

.link-row span {
  min-width: 0;
  flex: 1
}

.link-row b, .link-row small {
  display: block
}

.link-row b {
  color: #34364a;
  font-size: .25rem
}

.link-row small {
  margin-top: .03rem;
  color: #8b8ea0;
  font-size: .21rem;
  line-height: 1.4
}

.link-row > .van-icon:last-child {
  color: #aaa
}

.action-card > .van-button {
  height: .78rem;
  margin: .2rem 0 .08rem;
  border-radius: .12rem;
  font-size: .28rem
}

.execution-flow {
  margin: .22rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between
}

.execution-flow span {
  min-width: 0;
  text-align: center
}

.execution-flow b, .execution-flow small {
  display: block
}

.execution-flow b {
  color: #d32f2f;
  font-size: .25rem
}

.execution-flow small {
  margin-top: .03rem;
  color: #85899c;
  font-size: .2rem
}

.execution-flow em {
  color: #c2c4cd;
  font-size: .28rem;
  font-style: normal
}

.note {
  margin: .14rem .04rem;
  color: #85899c;
  font-size: .22rem;
  line-height: 1.55
}

.strategy-editor {
  width: 92%;
  max-height: 86vh;
  overflow: auto;
  padding: .22rem 0;
  border-radius: .2rem !important
}

.editor-title {
  display: flex;
  flex-direction: column;
  gap: .05rem;
  padding: .08rem .28rem .18rem;
  font-size: .3rem;
  font-weight: 600;
  color: #34364a
}

.editor-title small {
  font-size: .2rem;
  font-weight: 400;
  color: #8b8ea0
}

.strategy-editor /deep/ .van-cell {
  margin: .04rem .18rem;
  border: 1PX solid #ececf2;
  border-radius: .1rem;
  background: #fafaff
}

.strategy-editor /deep/ .van-cell__title {
  color: #555a70
}

.editor-actions {
  padding: .2rem .28rem
}

.managed-actions button {
  white-space: nowrap
}

/* Keep the strategy entry readable alongside the red navigation bar. */
.strategy-page .hero {
  background: #eef0f3;
  color: #30313a
}

.strategy-page .hero-kicker, .strategy-page .hero p {
  color: #687080;
  opacity: 1
}

.strategy-page .hero h1 {
  color: #30313a
}
.strategy-page { margin: 0; min-height: 100vh; background: #f6f7fb; padding-bottom: 1.7rem; }
.strategy-page /deep/ .van-nav-bar { margin: 0; width: 100%; height: 1.22667rem; border-radius: 0; background: linear-gradient(135deg,#351b1d,#c6282d); }
.strategy-page /deep/ .strategy-nav { position: relative !important; top: auto !important; padding-top: 0 !important; margin: 0 !important; width: 100% !important; border-radius: 0 !important; }
.strategy-page /deep/ .van-nav-bar__title { color: #fff; font-size: .36rem; line-height: 1.22667rem; }
.strategy-page /deep/ .van-nav-bar__left { height: 1.22667rem; }
</style>



// WEBPACK FOOTER //
// src/views/boss/StrategyExecution.vue